import React, { FC, ReactElement, useContext, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import { FormField } from "@natera/form/formField";
import { Option, Select } from "@natera/form/select";
import { Textfield } from "@natera/form/textfield";

import { ConfigContext } from "@app/provider";
import {
  getMaxDayForDob,
  getMonthsMap,
  type MonthsMapOption,
} from "@app/components/dob/months";

import "./dateOfBirthVerificationForm.scss";
import {
  ClearValidationError,
  GetValidationError,
} from "@natera/platform/lib/hooks/useError";

const messages = defineMessages({
  dateOfBirthVerificationMonthLabel: {
    id: "dateOfBirthVerificationMonthLabel",
    defaultMessage: "Month",
  },
  dateOfBirthVerificationDayLabel: {
    id: "dateOfBirthVerificationDayLabel",
    defaultMessage: "Day",
  },
  dateOfBirthVerificationYearLabel: {
    id: "dateOfBirthVerificationYearLabel",
    defaultMessage: "Year",
  },
  billingCenterGuestVerificationLabelDOB: {
    id: "billingCenterGuestVerificationLabelDOB",
    defaultMessage: "Date of Birth",
  },
});

interface DateOfBirthFormProps {
  month: Month | null;
  setMonth: (prop: Month | null) => void;
  day: string;
  setDay: (prop: string) => void;
  year: string;
  setYear: (prop: string) => void;
  getValidationError: GetValidationError;
  clearValidationError: ClearValidationError;
  publicLogin?: boolean;
}

export type Month = MonthsMapOption;

const DateOfBirthVerificationForm: FC<DateOfBirthFormProps> = ({
  month,
  setMonth,
  day,
  setDay,
  year,
  setYear,
  getValidationError,
  clearValidationError,
  publicLogin = false,
}) => {
  const intl = useIntl();
  const { config } = useContext(ConfigContext);

  const monthsMap = getMonthsMap(intl);

  const handleMonthChange = (value: string | string[] | number) => {
    const strValue = Array.isArray(value) ? value[0] : String(value);
    const match =
      monthsMap.find((option) => String(option.value) === strValue) ?? null;
    setMonth(match);
    if (match) {
      clearValidationError("dateOfBirthMonth");
    }
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const cleanedValue = inputValue.replace(/\D/g, "");
    const limitedValue = cleanedValue.slice(
      0,
      config.TEXT_FIELD_YEAR_MAX_LENGTH
    );

    event.target.value = limitedValue;
    setYear(limitedValue);
    clearValidationError("dateOfBirthYear");
  };

  const handleDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const cleanedValue = inputValue.replace(/\D/g, "");
    const limitedValue = cleanedValue.slice(
      0,
      config.TEXT_FIELD_DAY_MAX_LENGTH
    );

    event.target.value = limitedValue;
    setDay(limitedValue);
    clearValidationError("dateOfBirthDay");
  };

  const handleDayBlur = () => {
    const maxDay = getMaxDayForDob(month?.value ?? 0);
    const num = parseInt(day, 10);

    if (!day || isNaN(num) || num < 1 || num > maxDay) {
      setDay("");
    } else {
      setDay(num.toString());
    }
  };

  useEffect(() => {
    const maxDay = getMaxDayForDob(month?.value ?? 0);
    const num = parseInt(day, 10);
    if (day && (isNaN(num) || num > maxDay)) {
      setDay("");
    }
  }, [month]);

  const monthSelectElement: ReactElement = (
    <FormField
      label={intl.formatMessage(
        publicLogin
          ? messages.billingCenterGuestVerificationLabelDOB
          : messages.dateOfBirthVerificationMonthLabel
      )}
      error={getValidationError("dateOfBirthMonth")}
      htmlFor="dateOfBirthMonth"
    >
      <Select
        id="dateOfBirthMonth"
        name="dateOfBirthMonth"
        data-testid="dateOfBirthMonth"
        className={month ? "date-of-birth-month--selected" : undefined}
        placeholder={
          publicLogin
            ? intl.formatMessage(messages.dateOfBirthVerificationMonthLabel)
            : "MM"
        }
        value={month ? String(month.value) : undefined}
        onValueChange={handleMonthChange}
        outlined
        floating
      >
        {monthsMap.map((option) => (
          <Option key={option.value} value={String(option.value)}>
            {option.label}
          </Option>
        ))}
      </Select>
    </FormField>
  );

  const dayTextElement: ReactElement = (
    <FormField
      label={
        !publicLogin &&
        intl.formatMessage(messages.dateOfBirthVerificationDayLabel)
      }
      error={getValidationError("dateOfBirthDay")}
      htmlFor="dateOfBirthDay"
    >
      <Textfield
        placeholder={
          publicLogin
            ? intl.formatMessage(messages.dateOfBirthVerificationDayLabel)
            : "DD"
        }
        type="text"
        id="dateOfBirthDay"
        maxLength={config.TEXT_FIELD_DAY_MAX_LENGTH}
        name="dateOfBirthDay"
        data-testid="dateOfBirthDay"
        outline
        value={day}
        onChange={handleDayChange}
        onBlur={handleDayBlur}
      />
    </FormField>
  );

  const yearTextElement: ReactElement = (
    <FormField
      label={
        !publicLogin &&
        intl.formatMessage(messages.dateOfBirthVerificationYearLabel)
      }
      error={getValidationError("dateOfBirthYear")}
      htmlFor="dateOfBirthYear"
    >
      <Textfield
        placeholder={
          publicLogin
            ? intl.formatMessage(messages.dateOfBirthVerificationYearLabel)
            : "YYYY"
        }
        type="text"
        id="dateOfBirthYear"
        maxLength={config.TEXT_FIELD_YEAR_MAX_LENGTH}
        name="dateOfBirthYear"
        data-testid="dateOfBirthYear"
        outline
        onChange={handleYearChange}
        fullWidth
        value={year}
      />
    </FormField>
  );

  return (
    <div className="date-of-birth-verification-form">
      <div className="date-of-birth-form-row">
        {monthSelectElement}
        <div className={`${publicLogin && "date-of-birth-form-row__day"}`}>
          {dayTextElement}
        </div>
      </div>
      {yearTextElement}
    </div>
  );
};

export default DateOfBirthVerificationForm;
